/**
 * F38. Drawer lateral con el desglose de cuentas que componen una variable
 * (numerador o denominador) de un KPI. Se abre al hacer click en un scorecard.
 */

import { X } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import type { AccountRow } from '@toga/shared'
import { formatCents } from '../../lib/money'

interface KpiDrilldownDrawerProps {
  open: boolean
  onClose: () => void
  title: string
  accounts: AccountRow[]
}

export default function KpiDrilldownDrawer({
  open,
  onClose,
  title,
  accounts,
}: KpiDrilldownDrawerProps) {
  const { t } = useTranslation('accounting')
  const total = accounts.reduce((acc, a) => acc + a.amount, 0)

  return (
    <>
      {/* Overlay */}
      <button
        type="button"
        aria-label={t('kpiDrilldown.close')}
        onClick={onClose}
        className={[
          'fixed inset-0 z-40 bg-black/50 backdrop-blur-sm transition-opacity duration-200',
          open ? 'opacity-100' : 'pointer-events-none opacity-0',
        ].join(' ')}
      />

      {/* Panel */}
      <aside
        className={[
          'fixed top-0 right-0 z-50 h-screen w-full max-w-md flex flex-col',
          'border-l border-[var(--border)] bg-[var(--bg-panel)] shadow-2xl',
          'transition-transform duration-300',
          open ? 'translate-x-0' : 'translate-x-full',
        ].join(' ')}
        style={{ background: 'var(--bg-panel, var(--bg-input-soft))' }}
        aria-hidden={!open}
      >
        <header className="flex items-center justify-between gap-3 px-5 py-4 border-b border-[var(--border)]">
          <div className="min-w-0">
            <h2 className="font-semibold t-primary truncate">{title}</h2>
            <p className="text-xs t-muted mt-0.5">
              {accounts.length} {accounts.length === 1 ? t('kpiDrilldown.accountSingular') : t('kpiDrilldown.accountPlural')}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label={t('kpiDrilldown.close')}
            className="p-1.5 rounded-md hover:bg-[var(--bg-hover)] t-secondary"
          >
            <X size={18} />
          </button>
        </header>

        <div className="flex-1 overflow-y-auto">
          {accounts.length === 0 ? (
            <p className="px-5 py-8 text-sm t-muted text-center">
              {t('kpiDrilldown.emptyState')}
            </p>
          ) : (
            <table className="w-full text-sm">
              <thead className="bg-[var(--bg-input-soft)] t-muted text-xs">
                <tr>
                  <th className="text-left px-4 py-2 font-medium">{t('kpiDrilldown.columns.code')}</th>
                  <th className="text-left px-4 py-2 font-medium">{t('kpiDrilldown.columns.name')}</th>
                  <th className="text-right px-4 py-2 font-medium">{t('kpiDrilldown.columns.balance')}</th>
                </tr>
              </thead>
              <tbody>
                {accounts.map((a) => (
                  <tr
                    key={a.code}
                    className="border-t border-[var(--border)]/60"
                  >
                    <td className="px-4 py-2 font-mono text-xs t-muted whitespace-nowrap">
                      {a.code}
                    </td>
                    <td className="px-4 py-2 t-secondary">{a.name}</td>
                    <td className="px-4 py-2 text-right font-mono t-primary whitespace-nowrap">
                      {formatCents(a.amount)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        <footer className="px-5 py-3 border-t border-[var(--border)] bg-[var(--bg-hover)] flex items-center justify-between">
          <span className="text-sm font-semibold t-primary">{t('kpiDrilldown.total')}</span>
          <span className="font-mono font-semibold t-primary">
            {formatCents(total)}
          </span>
        </footer>
      </aside>
    </>
  )
}
